import React, { useMemo } from "react";

import {
	Badge,
	Box,
	ClickAwayListener,
	IconButton,
	List,
	ListItem,
	Tab,
	Tabs,
	Tooltip,
	Typography,
	useTheme
} from "@material-ui/core";
import { Notifications as NotificationsIcon } from "@material-ui/icons";

import { differenceInMilliseconds, parseISO } from "date-fns";

import { useStyles } from "./styles";

import { ISocketNotificationItem } from "../../models";
import { IExtendedTheme } from "../../theme/default";
import { Avatar } from "../Avatar";

enum FilterTabs {
	All = "All",
	Read = "Read",
	Unread = "UnRead"
}

const formatTimeAgo = (diffMs: number): string => {
	const seconds = Math.floor(diffMs / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);
	const years = Math.floor(days / 365);

	if (years >= 1) {
		return "long time ago";
	} else if (days >= 1) {
		return `${days} day${days !== 1 ? "s" : ""} ago`;
	} else if (hours >= 1) {
		return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
	} else if (minutes >= 1) {
		return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
	} else {
		const sec = Math.max(seconds, 0); // prevent negative seconds
		return `${sec} second${sec !== 1 ? "s" : ""} ago`;
	}
};

/**
 * Props for the BellNotificationsBlock component.
 * @typedef {Object} BellNotificationsBlockProps
 * @property {ISocketNotificationItem[]} bellNotifications - Array of notification items.
 * @property {function} [markAsRead] - Function to mark notifications as read.
 * @property {number} [unreadCount] - Number of unread notifications.
 */
type BellNotificationsBlockProps = {
	bellNotifications: ISocketNotificationItem[];
	markAsRead?: (notificationId?: number) => void;
	unreadCount?: number;
};

/**
 * BellNotificationsBlock component displays a notification bell icon with a badge showing the number of unread notifications.
 * When clicked, it shows a list of notifications.
 *
 * @param {BellNotificationsBlockProps} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * const notifications = [
 *   { id: 1, notification: { body: "New message received" }, readAt: null, action: () => console.log("Notification clicked") },
 *   { id: 2, notification: { body: "Your order has been shipped" }, readAt: new Date(), action: () => console.log("Notification clicked") }
 * ];
 *
 * <BellNotificationsBlock
 *   bellNotifications={notifications}
 *   markAsRead={() => console.log("Marked as read")}
 *   unreadCount={1}
 * />
 */
const BellNotificationsBlock = ({
	bellNotifications,
	markAsRead = () => {},
	unreadCount
}: BellNotificationsBlockProps) => {
	const theme = useTheme<IExtendedTheme>();
	const classes = useStyles();
	const [expanded, setExpanded] = React.useState(false);
	const [activeTab, setActiveTab] = React.useState<FilterTabs>(FilterTabs.All);

	const newItemsNumber = useMemo(
		() => unreadCount ?? bellNotifications.filter(n => !n.readAt).length,
		[bellNotifications, unreadCount]
	);

	const notifications = useMemo(() => {
		switch (activeTab) {
			case FilterTabs.Read:
				return bellNotifications.filter(n => n.readAt);
			case FilterTabs.Unread:
				return bellNotifications.filter(n => !n.readAt);
			default:
				return bellNotifications;
		}
	}, [activeTab, bellNotifications]);

	return (
		<Tooltip title="Notifications">
			<IconButton
				size="small"
				onClick={() => {
					if (!expanded && newItemsNumber) {
						markAsRead();
					}
					setExpanded(s => !s);
				}}
			>
				<Badge badgeContent={newItemsNumber > 99 ? "99+" : newItemsNumber} color="primary">
					<NotificationsIcon style={{ width: "20px", height: "20px", fill: "hsl(224,16%,41%)" }} />
				</Badge>
				{expanded && (
					<ClickAwayListener onClickAway={() => setExpanded(false)}>
						<Box className={classes.notificationWrapper}>
							<Box display="flex" justifyContent="space-between" mb={3}>
								<Typography className={classes.titleText} variant={"subtitle1"}>
									Notifications
								</Typography>
								<Typography
									className={classes.marAsReadText}
									onClick={() => {
										if (newItemsNumber) {
											markAsRead();
										}
									}}
								>
									Mark all read
								</Typography>
							</Box>

							<Tabs
								classes={{ indicator: classes.indicator }}
								value={activeTab}
								onClick={e => e.stopPropagation()}
								onChange={(_, v) => setActiveTab(v)}
								indicatorColor="primary"
							>
								{Object.values(FilterTabs).map(value => (
									<Tab
										onClick={e => e.stopPropagation()}
										classes={{ root: classes.tab, selected: classes.tabSelected }}
										key={value}
										label={value}
										value={value}
									/>
								))}
							</Tabs>

							<List style={{ background: theme.palette.background.paper, maxHeight: "350px", overflow: "auto" }}>
								{bellNotifications.length === 0 && (
									<ListItem style={{ justifyContent: "center" }}>No notifications yet</ListItem>
								)}
								{notifications.map(({ action, id, notification, readAt }) => (
									<ListItem
										key={id}
										onClick={() => {
											markAsRead(notification.id);
											action && action();
										}}
										className={classes.notificationBlock}
									>
										<Avatar variant="rounded" />
										<Box mx={2} flex={2}>
											<Typography className={classes.notificationTitle}>{notification.title}</Typography>
											<Typography className={classes.notificationBody}>{notification.body}</Typography>
										</Box>
										{!readAt && (
											<Box display="flex" flexDirection="column" alignItems="flex-end" gridGap={1}>
												<Box className={classes.markerIcon} />
												<Typography className={classes.notificationDate}>
													{formatTimeAgo(differenceInMilliseconds(Date.now(), parseISO(notification.createdAt || "")))}
												</Typography>
											</Box>
										)}
									</ListItem>
								))}
							</List>
						</Box>
					</ClickAwayListener>
				)}
			</IconButton>
		</Tooltip>
	);
};

export default BellNotificationsBlock;
